import React, { useState, useEffect, useRef, useCallback } from 'react';
import { GammaLensLogo } from './LandingPage';

/* ── COLOR GUIDE (dark bg: #030712 to #0f172a) ────────────────
   #f1f5f9  Primary text — prices, headings, key values
   #e2e8f0  Secondary headings
   #94a3b8  Body text, descriptions, labels         ← use this+
   #64748b  Muted helper text, timestamps           ← minimum readable
   #475569  Very muted, truly secondary only
   NEVER use #334155 or #1e293b as text color — invisible
─────────────────────────────────────────────────────────────── */
import {
  Chart as ChartJS,
  ArcElement,
  BarElement,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Doughnut, Bar, Line } from 'react-chartjs-2';

ChartJS.register(
  ArcElement, BarElement, LineElement, PointElement,
  CategoryScale, LinearScale, Tooltip, Legend, Filler
);

const API_BASE = process.env.NODE_ENV === 'production'
  ? 'https://gammalens-api.onrender.com'
  : 'http://localhost:8000';

const SECRET = '0722';

const REGIME_COLORS = {
  SIDEWAYS:      '#0a84ff',
  VOLATILE:      '#E24B4A',
  MOMENTUM_UP:   '#639922',
  MOMENTUM_DOWN: '#EF9F27',
  MIXED:         '#64748b',
};

const REGIME_BG = {
  SIDEWAYS:      'rgba(55,138,221,0.12)',
  VOLATILE:      'rgba(226,75,74,0.12)',
  MOMENTUM_UP:   'rgba(99,153,34,0.12)',
  MOMENTUM_DOWN: 'rgba(239,159,39,0.12)',
  MIXED:         'rgba(100,116,139,0.12)',
};

async function apiFetch(path) {
  const res = await fetch(`${API_BASE}${path}`);
  if (!res.ok) throw new Error(`API error ${res.status}`);
  return res.json();
}

// ─── Secret Gate ────────────────────────────────────────────────────────────

function SecretGate({ onUnlock }) {
  const [input, setInput] = useState('');
  const [error, setError] = useState(false);
  const [shake, setShake] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (input === SECRET) {
      onUnlock();
    } else {
      setError(true);
      setShake(true);
      setTimeout(() => setShake(false), 500);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: '#0a0f1e',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;500;600;700&display=swap');
        .gate-shake { animation: shake 0.4s ease; }
        @keyframes shake {
          0%,100% { transform: translateX(0); }
          20%,60% { transform: translateX(-8px); }
          40%,80% { transform: translateX(8px); }
        }
        .gate-input {
          width: 100%;
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.1);
          border-radius: 8px;
          padding: 14px 16px;
          color: #e2e8f0;
          font-family: inherit;
          font-size: 14px;
          letter-spacing: 0.1em;
          outline: none;
          transition: border-color 0.2s;
        }
        .gate-input:focus { border-color: rgba(55,138,221,0.6); }
        .gate-btn {
          width: 100%;
          padding: 14px;
          background: rgba(55,138,221,0.15);
          border: 1px solid rgba(55,138,221,0.3);
          border-radius: 8px;
          color: #4da6ff;
          font-family: inherit;
          font-size: 13px;
          font-weight: 500;
          letter-spacing: 0.08em;
          cursor: pointer;
          transition: all 0.2s;
          margin-top: 12px;
        }
        .gate-btn:hover { background: rgba(55,138,221,0.25); border-color: rgba(55,138,221,0.5); }
      `}</style>
      <div className={shake ? 'gate-shake' : ''} style={{ width: 340 }}>
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{ fontSize: 11, letterSpacing: '0.2em', color: '#475569', marginBottom: 8 }}>
            GAMMALENS
          </div>
          <div style={{ fontSize: 22, fontWeight: 700, color: '#e2e8f0', marginBottom: 4 }}>
            Admin Dashboard
          </div>
          <div style={{ fontSize: 12, color: '#475569' }}>Historical GEX Analysis</div>
        </div>
        <form onSubmit={handleSubmit}>
          <input
            className="gate-input"
            type="password"
            placeholder="Enter secret key"
            value={input}
            onChange={e => { setInput(e.target.value); setError(false); }}
            autoFocus
            style={error ? { borderColor: 'rgba(226,75,74,0.6)' } : {}}
          />
          {error && (
            <div style={{ fontSize: 11, color: '#E24B4A', marginTop: 6, letterSpacing: '0.05em' }}>
              Invalid secret key
            </div>
          )}
          <button className="gate-btn" type="submit">ACCESS DASHBOARD →</button>
        </form>
      </div>
    </div>
  );
}

// ─── Metric Card ─────────────────────────────────────────────────────────────

function MetricCard({ label, value, sub }) {
  return (
    <div style={{
      background: 'rgba(255,255,255,0.03)',
      border: '1px solid rgba(255,255,255,0.06)',
      borderRadius: 10,
      padding: '16px 18px',
    }}>
      <div style={{ fontSize: 11, color: '#94a3b8', letterSpacing: '0.08em', marginBottom: 6 }}>
        {label}
      </div>
      <div style={{ fontSize: 22, fontWeight: 600, color: '#e2e8f0' }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: '#64748b', marginTop: 3 }}>{sub}</div>}
    </div>
  );
}

// ─── Regime Badge ────────────────────────────────────────────────────────────

function RegimeBadge({ regime }) {
  return (
    <span style={{
      display: 'inline-block',
      padding: '2px 8px',
      borderRadius: 4,
      fontSize: 11,
      fontWeight: 600,
      letterSpacing: '0.04em',
      background: REGIME_BG[regime] || REGIME_BG.MIXED,
      color: REGIME_COLORS[regime] || REGIME_COLORS.MIXED,
      border: `1px solid ${(REGIME_COLORS[regime] || REGIME_COLORS.MIXED)}33`,
    }}>
      {regime}
    </span>
  );
}

// ─── Loading / Error ─────────────────────────────────────────────────────────

function Loading() {
  return (
    <div style={{ padding: '40px 0', textAlign: 'center', color: '#475569', fontSize: 13 }}>
      Loading...
    </div>
  );
}

function ErrorMsg({ msg }) {
  return (
    <div style={{
      padding: '16px',
      background: 'rgba(226,75,74,0.08)',
      border: '1px solid rgba(226,75,74,0.2)',
      borderRadius: 8,
      color: '#E24B4A',
      fontSize: 13,
    }}>
      {msg}
    </div>
  );
}

// ─── Historical Overview Tab (renamed from OverviewTab to avoid collision) ───

function HistoricalOverviewTab({ summary, ticker }) {
  if (!summary) return <Loading />;

  const tickerInfo = summary.tickers.find(t => t.ticker === ticker);
  if (!tickerInfo) return <ErrorMsg msg={`No data for ${ticker} yet`} />;

  const regimes = summary.regime_distribution.filter(r => r.ticker === ticker);
  const byYear  = summary.by_year.filter(r => r.ticker === ticker);

  const regimeOrder = ['SIDEWAYS','VOLATILE','MOMENTUM_UP','MOMENTUM_DOWN','MIXED'];
  const sorted = regimeOrder.map(r => regimes.find(x => x.regime === r)).filter(Boolean);
  const total  = sorted.reduce((s, r) => s + parseInt(r.days), 0);
  const mixed  = sorted.find(r => r.regime === 'MIXED');
  const cleanPct = mixed ? Math.round((total - parseInt(mixed.days)) / total * 100) : 100;

  const years = [...new Set(byYear.map(r => r.year))].sort();
  const mainRegimes = ['SIDEWAYS','VOLATILE','MOMENTUM_UP','MOMENTUM_DOWN'];

  const doughnutData = {
    labels: sorted.map(r => r.regime),
    datasets: [{
      data: sorted.map(r => parseInt(r.days)),
      backgroundColor: sorted.map(r => REGIME_COLORS[r.regime]),
      borderWidth: 0,
    }],
  };

  const barData = {
    labels: years,
    datasets: mainRegimes.map(regime => ({
      label: regime,
      data: years.map(y => {
        const r = byYear.find(x => x.year === y && x.regime === regime);
        return r ? parseInt(r.days) : 0;
      }),
      backgroundColor: REGIME_COLORS[regime],
      borderWidth: 0,
    })),
  };

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginBottom: 28 }}>
        <MetricCard label="TRADING DAYS" value={parseInt(tickerInfo.total_days).toLocaleString()} sub={`${tickerInfo.from_date} → ${tickerInfo.to_date}`} />
        <MetricCard label="AVG VIX" value={tickerInfo.avg_vix} sub="20-year average" />
        <MetricCard label="AVG DAILY RANGE" value={`${tickerInfo.avg_range_pct}%`} sub="open to close" />
        <MetricCard label="CLEAN LABELS" value={`${cleanPct}%`} sub="non-MIXED days" />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 20, marginBottom: 28 }}>
        <div>
          <div style={sectionTitle}>Regime distribution</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
            {sorted.map(r => (
              <span key={r.regime} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: '#94a3b8' }}>
                <span style={{ width: 8, height: 8, borderRadius: 2, background: REGIME_COLORS[r.regime], display: 'inline-block' }} />
                {r.regime} {r.pct}%
              </span>
            ))}
          </div>
          <div style={{ height: 220 }}>
            <Doughnut data={doughnutData} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } } }} />
          </div>
        </div>
        <div>
          <div style={sectionTitle}>Regime by year</div>
          <div style={{ height: 260 }}>
            <Bar data={barData} options={{
              responsive: true,
              maintainAspectRatio: false,
              plugins: { legend: { display: false } },
              scales: {
                x: { stacked: true, ticks: { color: '#475569', font: { size: 10 } }, grid: { color: 'rgba(255,255,255,0.04)' } },
                y: { stacked: true, ticks: { color: '#475569', font: { size: 10 } }, grid: { color: 'rgba(255,255,255,0.04)' } },
              },
            }} />
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Patterns Tab ────────────────────────────────────────────────────────────

function PatternsTab({ ticker }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    apiFetch(`/api/admin/historical/patterns?secret=${SECRET}&ticker=${ticker}`)
      .then(d => { setData(d); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, [ticker]);

  if (loading) return <Loading />;
  if (error) return <ErrorMsg msg={error} />;

  const mainRegimes = ['SIDEWAYS','VOLATILE','MOMENTUM_UP','MOMENTUM_DOWN'];
  const gexGroups = [...new Set(data.gex_patterns.map(r => r.gex_sign))];
  const vixBuckets = [...new Set(data.vix_patterns.map(r => r.vix_bucket))].sort();

  const gexData = {
    labels: gexGroups,
    datasets: mainRegimes.map(regime => ({
      label: regime,
      data: gexGroups.map(g => {
        const r = data.gex_patterns.find(x => x.gex_sign === g && x.regime === regime);
        return r ? parseFloat(r.pct) : 0;
      }),
      backgroundColor: REGIME_COLORS[regime],
      borderWidth: 0,
    })),
  };

  const vixData = {
    labels: vixBuckets.map(b => b.replace(/^\d\. /, '')),
    datasets: mainRegimes.map(regime => ({
      label: regime,
      data: vixBuckets.map(b => {
        const r = data.vix_patterns.find(x => x.vix_bucket === b && x.regime === regime);
        return r ? parseFloat(r.pct) : 0;
      }),
      backgroundColor: REGIME_COLORS[regime],
      borderWidth: 0,
    })),
  };

  const stackedOpts = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false } },
    scales: {
      x: { stacked: true, ticks: { color: '#475569', font: { size: 11 } }, grid: { color: 'rgba(255,255,255,0.04)' } },
      y: { stacked: true, ticks: { color: '#475569', font: { size: 11 }, callback: v => v + '%' }, grid: { color: 'rgba(255,255,255,0.04)' } },
    },
  };

  const days = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
  const dowData = days.map((d, i) => {
    const rows = data.dow_patterns.filter(r => r.dow === i);
    if (!rows.length) return null;
    const totalDays = rows.reduce((s, r) => s + parseInt(r.days), 0);
    const avg = rows.reduce((s, r) => s + parseFloat(r.avg_range_pct) * parseInt(r.days), 0) / totalDays;
    return { day: d, avg: parseFloat(avg.toFixed(3)) };
  }).filter(Boolean);

  const dowChartData = {
    labels: dowData.map(d => d.day),
    datasets: [{
      label: 'Avg range %',
      data: dowData.map(d => d.avg),
      backgroundColor: '#0a84ff',
      borderWidth: 0,
      borderRadius: 4,
    }],
  };

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 28 }}>
        <div>
          <div style={sectionTitle}>GEX sign → regime outcome</div>
          <LegendRow regimes={mainRegimes} />
          <div style={{ height: 220 }}><Bar data={gexData} options={stackedOpts} /></div>
        </div>
        <div>
          <div style={sectionTitle}>VIX level → regime outcome</div>
          <LegendRow regimes={mainRegimes} />
          <div style={{ height: 220 }}><Bar data={vixData} options={stackedOpts} /></div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        <div>
          <div style={sectionTitle}>P/C ratio — contrarian signal</div>
          {data.pc_patterns.map(r => {
            const pct = Math.min(Math.abs(parseFloat(r.avg_direction_pct)) * 150, 100);
            const isPos = parseFloat(r.avg_direction_pct) > 0;
            return (
              <div key={r.sentiment} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                <span style={{ fontSize: 12, color: '#94a3b8', minWidth: 160 }}>{r.sentiment.replace(/^\d\. /, '')}</span>
                <div style={{ flex: 1, height: 5, background: 'rgba(255,255,255,0.05)', borderRadius: 3 }}>
                  <div style={{ width: `${pct}%`, height: '100%', borderRadius: 3, background: isPos ? '#639922' : '#E24B4A' }} />
                </div>
                <span style={{ fontSize: 12, fontWeight: 600, color: isPos ? '#639922' : '#E24B4A', minWidth: 55, textAlign: 'right' }}>
                  {isPos ? '+' : ''}{parseFloat(r.avg_direction_pct).toFixed(3)}%
                </span>
                <span style={{ fontSize: 11, color: '#475569', minWidth: 50, textAlign: 'right' }}>
                  {parseInt(r.days).toLocaleString()}d
                </span>
              </div>
            );
          })}
        </div>
        <div>
          <div style={sectionTitle}>Day of week — avg range</div>
          <div style={{ height: 200 }}>
            <Bar data={dowChartData} options={{
              responsive: true,
              maintainAspectRatio: false,
              plugins: { legend: { display: false } },
              scales: {
                x: { ticks: { color: '#475569', font: { size: 11 } }, grid: { color: 'rgba(255,255,255,0.04)' } },
                y: { ticks: { color: '#475569', font: { size: 11 }, callback: v => v + '%' }, grid: { color: 'rgba(255,255,255,0.04)' } },
              },
            }} />
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Range Sizing Tab ────────────────────────────────────────────────────────

function RangesTab({ ticker }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    apiFetch(`/api/admin/historical/ranges?secret=${SECRET}&ticker=${ticker}`)
      .then(d => { setData(d); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, [ticker]);

  if (loading) return <Loading />;
  if (error) return <ErrorMsg msg={error} />;

  const regimeOrder = ['SIDEWAYS','VOLATILE','MOMENTUM_UP','MOMENTUM_DOWN','MIXED'];
  const sorted = regimeOrder.map(r => data.ranges_by_regime.find(x => x.regime === r)).filter(Boolean);
  const filtered = sorted.filter(r => r.regime !== 'MIXED');

  const chartData = {
    labels: filtered.map(r => r.regime),
    datasets: [
      {
        label: 'Avg range',
        data: filtered.map(r => parseFloat(r.avg_range_pct)),
        backgroundColor: filtered.map(r => REGIME_COLORS[r.regime] + '66'),
        borderWidth: 0,
        borderRadius: 4,
      },
      {
        label: 'P95 range',
        data: filtered.map(r => parseFloat(r.p95_range)),
        backgroundColor: filtered.map(r => REGIME_COLORS[r.regime]),
        borderWidth: 0,
        borderRadius: 4,
      },
    ],
  };

  return (
    <div>
      <div style={sectionTitle}>Daily range by regime — iron condor wing sizing guide</div>
      <div style={{ overflowX: 'auto', marginBottom: 28 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr>
              {['Regime','Days','Avg %','P25','P50','P75','P95 (worst)','Avg VIX'].map(h => (
                <th key={h} style={{ textAlign: 'left', padding: '8px 12px', borderBottom: '1px solid rgba(255,255,255,0.08)', color: '#475569', fontSize: 11, letterSpacing: '0.05em', fontWeight: 500 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sorted.map(r => (
              <tr key={r.regime} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                <td style={{ padding: '10px 12px' }}><RegimeBadge regime={r.regime} /></td>
                <td style={{ padding: '10px 12px', color: '#94a3b8' }}>{parseInt(r.days).toLocaleString()}</td>
                <td style={{ padding: '10px 12px', color: '#e2e8f0', fontWeight: 600 }}>{r.avg_range_pct}%</td>
                <td style={{ padding: '10px 12px', color: '#94a3b8' }}>{r.p25_range}%</td>
                <td style={{ padding: '10px 12px', color: '#94a3b8' }}>{r.p50_range}%</td>
                <td style={{ padding: '10px 12px', color: '#94a3b8' }}>{r.p75_range}%</td>
                <td style={{ padding: '10px 12px', color: '#E24B4A', fontWeight: 600 }}>{r.p95_range}%</td>
                <td style={{ padding: '10px 12px', color: '#94a3b8' }}>{r.avg_vix}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={sectionTitle}>Avg vs P95 range per regime</div>
      <div style={{ height: 240 }}>
        <Bar data={chartData} options={{
          responsive: true,
          maintainAspectRatio: false,
          plugins: { legend: { display: false } },
          scales: {
            x: { ticks: { color: '#475569', font: { size: 11 } }, grid: { color: 'rgba(255,255,255,0.04)' } },
            y: { ticks: { color: '#475569', font: { size: 11 }, callback: v => v + '%' }, grid: { color: 'rgba(255,255,255,0.04)' } },
          },
        }} />
      </div>
    </div>
  );
}

// ─── Seasonality Tab ─────────────────────────────────────────────────────────

function SeasonalityTab({ ticker }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    apiFetch(`/api/admin/historical/ranges?secret=${SECRET}&ticker=${ticker}`)
      .then(d => { setData(d); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, [ticker]);

  if (loading) return <Loading />;
  if (error) return <ErrorMsg msg={error} />;

  const months = data.monthly_seasonality;

  const rangeData = {
    labels: months.map(m => m.month_name),
    datasets: [{
      label: 'Avg range %',
      data: months.map(m => parseFloat(m.avg_range_pct)),
      backgroundColor: '#0a84ff',
      borderWidth: 0,
      borderRadius: 4,
    }],
  };

  const vixData = {
    labels: months.map(m => m.month_name),
    datasets: [{
      label: 'Avg VIX',
      data: months.map(m => parseFloat(m.avg_vix)),
      borderColor: '#E24B4A',
      backgroundColor: 'rgba(226,75,74,0.08)',
      fill: true,
      tension: 0.4,
      pointRadius: 5,
      pointBackgroundColor: '#E24B4A',
    }],
  };

  const commonOpts = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false } },
    scales: {
      x: { ticks: { color: '#475569', font: { size: 11 }, autoSkip: false }, grid: { color: 'rgba(255,255,255,0.04)' } },
      y: { ticks: { color: '#475569', font: { size: 11 } }, grid: { color: 'rgba(255,255,255,0.04)' } },
    },
  };

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        <div>
          <div style={sectionTitle}>Average daily range by month</div>
          <div style={{ height: 260 }}>
            <Bar data={rangeData} options={{ ...commonOpts, scales: { ...commonOpts.scales, y: { ...commonOpts.scales.y, ticks: { ...commonOpts.scales.y.ticks, callback: v => v + '%' } } } }} />
          </div>
        </div>
        <div>
          <div style={sectionTitle}>Average VIX by month</div>
          <div style={{ height: 260 }}>
            <Line data={vixData} options={commonOpts} />
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Legend Row ──────────────────────────────────────────────────────────────

function LegendRow({ regimes }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 10 }}>
      {regimes.map(r => (
        <span key={r} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: '#64748b' }}>
          <span style={{ width: 8, height: 8, borderRadius: 2, background: REGIME_COLORS[r], display: 'inline-block' }} />
          {r}
        </span>
      ))}
    </div>
  );
}

// ─── Shared styles ───────────────────────────────────────────────────────────

const sectionTitle = {
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: '0.1em',
  color: '#475569',
  marginBottom: 14,
  textTransform: 'uppercase',
};

// ─── Today's Verdict Tab ─────────────────────────────────────────────────────

const SIGNAL_STYLES = {
  'MOMENTUM UP':   { color: '#639922', bg: 'rgba(99,153,34,0.1)',    border: 'rgba(99,153,34,0.25)',   icon: '▲' },
  'MOMENTUM DOWN': { color: '#E24B4A', bg: 'rgba(226,75,74,0.1)',    border: 'rgba(226,75,74,0.25)',   icon: '▼' },
  'SIDEWAYS':      { color: '#64748b', bg: 'rgba(100,116,139,0.1)',  border: 'rgba(100,116,139,0.2)', icon: '—' },
  // Legacy fallback
  'BUY CALLS': { color: '#639922', bg: 'rgba(99,153,34,0.1)',   border: 'rgba(99,153,34,0.25)',   icon: '▲' },
  'BUY PUTS':  { color: '#E24B4A', bg: 'rgba(226,75,74,0.1)',   border: 'rgba(226,75,74,0.25)',   icon: '▼' },
  'NO TRADE':  { color: '#64748b', bg: 'rgba(100,116,139,0.1)', border: 'rgba(100,116,139,0.2)', icon: '—' },
};

const CONVICTION_COLORS = {
  HIGH:   '#639922',
  MEDIUM: '#EF9F27',
  LOW:    '#E24B4A',
};

function TickerSignalRow({ ticker, signal, confidence, regime, spot, target, stop }) {
  const s = SIGNAL_STYLES[signal] || SIGNAL_STYLES['NO TRADE'];
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: '60px 130px 80px 100px 90px 90px 90px',
      alignItems: 'center',
      padding: '12px 0',
      borderBottom: '1px solid rgba(255,255,255,0.05)',
      gap: 8,
    }}>
      <span style={{ fontSize: 13, fontWeight: 700, color: '#e2e8f0' }}>{ticker}</span>
      <span style={{
        padding: '4px 10px',
        borderRadius: 5,
        fontSize: 11,
        fontWeight: 700,
        background: s.bg,
        color: s.color,
        border: `1px solid ${s.border}`,
        textAlign: 'center',
        letterSpacing: '0.04em',
      }}>
        {s.icon} {signal}
      </span>
      <span style={{ fontSize: 12, color: '#64748b' }}>{confidence}%</span>
      <span style={{ fontSize: 11, color: '#475569' }}>{regime}</span>
      <span style={{ fontSize: 12, color: '#94a3b8' }}>${spot || '—'}</span>
      <span style={{ fontSize: 12, color: signal !== 'NO TRADE' ? s.color : '#475569', fontWeight: signal !== 'NO TRADE' ? 600 : 400 }}>
        {target ? `$${target}` : '—'}
      </span>
      <span style={{ fontSize: 12, color: '#E24B4A' }}>{stop ? `$${stop}` : '—'}</span>
    </div>
  );
}

function VerdictTab() {
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [history, setHistory]   = useState([]);
  const [generating, setGenerating] = useState(false);

  const fetchVerdict = () => {
    setLoading(true);
    setError(null);
    Promise.all([
      apiFetch(`/api/admin/verdict/today?secret=${SECRET}`),
      apiFetch(`/api/admin/verdict/history?secret=${SECRET}&days=7`),
    ])
      .then(([v, h]) => {
        setData(v);
        setHistory(h.verdicts || []);
        setLoading(false);
      })
      .catch(e => { setError(e.message); setLoading(false); });
  };

  const triggerGenerate = () => {
    setGenerating(true);
    fetch(`${API_BASE}/api/admin/verdict/generate?secret=${SECRET}`, { method: 'POST' })
      .then(r => r.json())
      .then(() => {
        setTimeout(() => {
          fetchVerdict();
          setGenerating(false);
        }, 18000);
      })
      .catch(e => { setError(e.message); setGenerating(false); });
  };

  useEffect(() => { fetchVerdict(); }, []);

  if (loading) return <Loading />;
  if (error) return <ErrorMsg msg={error} />;

  const overallSignal = data?.overall_signal || 'NO TRADE';
  const conviction    = data?.overall_conviction || 'LOW';
  const overallStyle  = SIGNAL_STYLES[overallSignal] || SIGNAL_STYLES['NO TRADE'];
  const convColor     = CONVICTION_COLORS[conviction] || '#64748b';

  return (
    <div>
      {data?.available ? (
        <div style={{ marginBottom: 28 }}>

          {/* Overall conviction banner */}
          <div style={{
            background: overallStyle.bg,
            border: `1px solid ${overallStyle.border}`,
            borderRadius: 12,
            padding: '20px 24px',
            marginBottom: 16,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <div style={{ fontSize: 26, fontWeight: 700, color: overallStyle.color, letterSpacing: '0.04em' }}>
                {overallStyle.icon} {overallSignal}
              </div>
              <div style={{
                padding: '4px 12px',
                borderRadius: 6,
                background: `${convColor}22`,
                border: `1px solid ${convColor}44`,
                fontSize: 11,
                fontWeight: 700,
                color: convColor,
                letterSpacing: '0.08em',
              }}>
                {conviction} CONVICTION
              </div>
              <div style={{ fontSize: 12, color: '#64748b' }}>
                VIX {data.vix} · P/C {data.pc_ratio}
              </div>
            </div>
            <div style={{ textAlign: 'right', fontSize: 11, color: '#475569' }}>
              <div>{data.date}</div>
              <div style={{ marginTop: 2 }}>
                {new Date(data.created_at).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })} ET
              </div>
            </div>
          </div>

          {/* Per-ticker breakdown */}
          <div style={{
            background: 'rgba(255,255,255,0.02)',
            border: '1px solid rgba(255,255,255,0.06)',
            borderRadius: 10,
            padding: '4px 20px 0',
            marginBottom: 16,
          }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: '60px 130px 80px 100px 90px 90px 90px',
              padding: '8px 0',
              gap: 8,
              borderBottom: '1px solid rgba(255,255,255,0.06)',
            }}>
              {['TICKER','SIGNAL','CONF','REGIME','SPOT','TARGET','STOP'].map(h => (
                <span key={h} style={{ fontSize: 10, color: '#64748b', letterSpacing: '0.08em', fontWeight: 600 }}>{h}</span>
              ))}
            </div>
            <TickerSignalRow
              ticker="SPY"
              signal={data.spy_signal}
              confidence={data.spy_confidence}
              regime={data.spy_regime}
              spot={data.spy_spot}
              target={data.spy_target}
              stop={data.spy_stop}
            />
            <TickerSignalRow
              ticker="QQQ"
              signal={data.qqq_signal}
              confidence={data.qqq_confidence}
              regime={data.qqq_regime}
              spot={data.qqq_spot}
              target={data.qqq_target}
              stop={data.qqq_stop}
            />
            <TickerSignalRow
              ticker="IWM"
              signal={data.iwm_signal}
              confidence={data.iwm_confidence}
              regime={data.iwm_regime}
              spot={data.iwm_spot}
              target={data.iwm_target}
              stop={data.iwm_stop}
            />
          </div>

          {/* Claude verdict text */}
          <div style={{
            background: 'rgba(255,255,255,0.02)',
            border: '1px solid rgba(255,255,255,0.06)',
            borderRadius: 10,
            padding: '18px 20px',
            fontSize: 13,
            color: '#cbd5e1',
            lineHeight: 1.8,
            whiteSpace: 'pre-wrap',
            fontFamily: 'inherit',
            marginBottom: 14,
          }}>
            {data.verdict}
          </div>

          {/* Action buttons */}
          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={fetchVerdict} style={btnStyle}>REFRESH ↻</button>
            <button
              onClick={triggerGenerate}
              disabled={generating}
              style={{ ...btnStyle, background: 'rgba(55,138,221,0.08)', borderColor: 'rgba(55,138,221,0.2)', color: generating ? '#475569' : '#4da6ff' }}
            >
              {generating ? 'GENERATING... (~15s)' : 'REGENERATE →'}
            </button>
          </div>
        </div>
      ) : (
        <div style={{
          padding: '48px 24px',
          background: 'rgba(255,255,255,0.02)',
          border: '1px solid rgba(255,255,255,0.06)',
          borderRadius: 12,
          textAlign: 'center',
          marginBottom: 28,
        }}>
          <div style={{ fontSize: 13, color: '#475569', marginBottom: 10 }}>
            No verdict yet for today
          </div>
          <div style={{ fontSize: 12, color: '#64748b', marginBottom: 24 }}>
            Auto-generates at 9:35 AM ET from SPY · QQQ · IWM data
          </div>
          <button
            onClick={triggerGenerate}
            disabled={generating}
            style={{
              padding: '10px 22px',
              background: 'rgba(55,138,221,0.1)',
              border: '1px solid rgba(55,138,221,0.25)',
              borderRadius: 8,
              color: '#4da6ff',
              fontSize: 12,
              letterSpacing: '0.06em',
              cursor: generating ? 'not-allowed' : 'pointer',
              fontFamily: 'inherit',
              fontWeight: 600,
            }}
          >
            {generating ? 'GENERATING... (~15s)' : 'GENERATE NOW →'}
          </button>
        </div>
      )}

      {/* History */}
      {history.length > 0 && (
        <div>
          <div style={sectionTitle}>Recent verdicts</div>
          {history.map((v, i) => {
            const os = SIGNAL_STYLES[v.overall_signal] || SIGNAL_STYLES['NO TRADE'];
            const cc = CONVICTION_COLORS[v.overall_conviction] || '#64748b';
            return (
              <div key={i} style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '10px 0',
                borderBottom: '1px solid rgba(255,255,255,0.04)',
                flexWrap: 'wrap',
              }}>
                <span style={{ fontSize: 11, color: '#475569', minWidth: 80 }}>{v.date}</span>
                <span style={{
                  padding: '2px 10px', borderRadius: 4, fontSize: 11, fontWeight: 700,
                  background: os.bg, color: os.color, border: `1px solid ${os.border}`,
                }}>
                  {os.icon} {v.overall_signal}
                </span>
                <span style={{ fontSize: 11, color: cc, fontWeight: 600 }}>{v.overall_conviction}</span>
                <span style={{ fontSize: 10, color: '#475569' }}>
                  SPY:{v.spy_signal?.replace('MOMENTUM ','')} · QQQ:{v.qqq_signal?.replace('MOMENTUM ','')} · IWM:{v.iwm_signal?.replace('MOMENTUM ','')}
                </span>
                <span style={{ fontSize: 11, color: '#475569', marginLeft: 'auto' }}>VIX {v.vix}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const btnStyle = {
  padding: '8px 16px',
  background: 'none',
  border: '1px solid rgba(255,255,255,0.08)',
  borderRadius: 6,
  color: '#64748b',
  fontSize: 11,
  letterSpacing: '0.06em',
  cursor: 'pointer',
  fontFamily: 'inherit',
};


// ─── AI Interpret Tab ────────────────────────────────────────────────────────

function InterpretTab({ ticker }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);
  const [fetched, setFetched] = useState(null); // track which ticker was fetched

  const fetchInterpretation = () => {
    setLoading(true);
    setError(null);
    setData(null);
    apiFetch(`/api/admin/historical/interpret?secret=${SECRET}&ticker=${ticker}`)
      .then(d => { setData(d); setFetched(ticker); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  };

  // Format interpretation text into sections
  const formatText = (text) => {
    if (!text) return null;
    const lines = text.split('\n');
    return lines.map((line, i) => {
      // Section headers (numbered like "1. TOP 3...")
      if (/^\d+\.\s+[A-Z]/.test(line)) {
        return (
          <div key={i} style={{
            fontSize: 11, fontWeight: 700, letterSpacing: '0.12em',
            color: '#4da6ff', marginTop: 20, marginBottom: 8,
            textTransform: 'uppercase',
          }}>
            {line}
          </div>
        );
      }
      // Bullet points
      if (line.trim().startsWith('→') || line.trim().startsWith('-') || line.trim().startsWith('•')) {
        return (
          <div key={i} style={{
            fontSize: 13, color: '#94a3b8', paddingLeft: 16,
            marginBottom: 4, lineHeight: 1.6,
          }}>
            {line}
          </div>
        );
      }
      // Bold-like lines (ALL CAPS words)
      if (line.trim() === '') {
        return <div key={i} style={{ height: 6 }} />;
      }
      // Normal text
      return (
        <div key={i} style={{
          fontSize: 13, color: '#cbd5e1', marginBottom: 4, lineHeight: 1.7,
        }}>
          {line}
        </div>
      );
    });
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 600, color: '#e2e8f0', marginBottom: 4 }}>
            AI Pattern Interpretation
          </div>
          <div style={{ fontSize: 12, color: '#475569' }}>
            Claude analyzes {ticker} patterns and explains what they mean for your trading
          </div>
        </div>
        <button
          onClick={fetchInterpretation}
          disabled={loading}
          style={{
            padding: '10px 20px',
            background: loading ? 'rgba(55,138,221,0.05)' : 'rgba(55,138,221,0.12)',
            border: '1px solid rgba(55,138,221,0.3)',
            borderRadius: 8,
            color: loading ? '#475569' : '#4da6ff',
            fontSize: 12,
            fontWeight: 600,
            letterSpacing: '0.08em',
            cursor: loading ? 'not-allowed' : 'pointer',
            fontFamily: 'inherit',
            transition: 'all 0.2s',
            whiteSpace: 'nowrap',
          }}
        >
          {loading ? 'ANALYZING...' : fetched === ticker ? 'REFRESH ↻' : 'INTERPRET WITH AI →'}
        </button>
      </div>

      {!data && !loading && !error && (
        <div style={{
          padding: '40px 24px',
          background: 'rgba(255,255,255,0.02)',
          border: '1px solid rgba(255,255,255,0.06)',
          borderRadius: 10,
          textAlign: 'center',
        }}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>🧠</div>
          <div style={{ fontSize: 14, color: '#475569', marginBottom: 6 }}>
            Ready to interpret {ticker} patterns
          </div>
          <div style={{ fontSize: 12, color: '#64748b' }}>
            Click "Interpret with AI" to get plain English trading insights
          </div>
        </div>
      )}

      {loading && (
        <div style={{
          padding: '40px 24px',
          background: 'rgba(55,138,221,0.04)',
          border: '1px solid rgba(55,138,221,0.1)',
          borderRadius: 10,
          textAlign: 'center',
        }}>
          <div style={{ fontSize: 13, color: '#475569', marginBottom: 8 }}>
            Analyzing {ticker} patterns across 5,000+ trading days...
          </div>
          <div style={{ fontSize: 11, color: '#64748b' }}>
            This takes 10-15 seconds
          </div>
        </div>
      )}

      {error && <ErrorMsg msg={error} />}

      {data && !loading && (
        <div style={{
          background: 'rgba(255,255,255,0.02)',
          border: '1px solid rgba(255,255,255,0.06)',
          borderRadius: 10,
          padding: '24px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20, paddingBottom: 16, borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
            <span style={{ fontSize: 11, color: '#475569' }}>
              {parseInt(data.total_days).toLocaleString()} days analyzed
            </span>
            <span style={{ color: 'rgba(255,255,255,0.1)' }}>·</span>
            <span style={{ fontSize: 11, color: '#475569' }}>
              {data.from_date} → {data.to_date}
            </span>
            <span style={{ color: 'rgba(255,255,255,0.1)' }}>·</span>
            <span style={{ fontSize: 11, color: '#639922' }}>
              claude-haiku
            </span>
          </div>
          <div>{formatText(data.interpretation)}</div>
        </div>
      )}
    </div>
  );
}

// ─── Overview Tab ─────────────────────────────────────────────────────────

// ─── SPY Movement Reference (STATIC) ─────────────────────────────────────────
// One-time 10-year study of SPY's intraday "path" — the sum of absolute
// 15-min close-to-close moves inside the regular session (09:30–16:00 ET).
// Period: 2016-01-04 → 2026-10-16, 2,713 trading days.
//
// INTENTIONALLY HARDCODED. Do NOT add a fetch, a scheduler, or a backend
// endpoint for this — the numbers barely budge day-to-day, so a live refresh
// would just burn MarketData credits + compute for no reader-visible change.
// If/when the study is re-run, update the constants in SPY_REF below.
const SPY_REF = {
  source: 'SPY · 15-min bars · regular session (09:30–16:00 ET) · 2016-01-04 to 2026-10-16 · 2,713 trading days',
  headline: { avgPath: 2.46, medianPath: 1.96, avgRange: 0.89, pathToRange: 2.75 },
  byYear: [
    { y: 2016, p: 2.090, d: 252 },
    { y: 2017, p: 1.163, d: 251 },
    { y: 2018, p: 2.719, d: 251 },
    { y: 2019, p: 1.880, d: 252 },
    { y: 2020, p: 4.028, d: 253 },
    { y: 2021, p: 2.099, d: 252 },
    { y: 2022, p: 3.936, d: 251 },
    { y: 2023, p: 2.292, d: 250 },
    { y: 2024, p: 1.996, d: 252 },
    { y: 2025, p: 2.657, d: 250 },
    { y: 2026, p: 2.089, d: 199 },
  ],
  byWeekday: [
    { w: 'Mon', p: 2.291, d: 506 },
    { w: 'Tue', p: 2.377, d: 559 },
    { w: 'Wed', p: 2.502, d: 556 },
    { w: 'Thu', p: 2.643, d: 547 },
    { w: 'Fri', p: 2.461, d: 545 },
  ],
  distribution: [
    { label: '10th',   v: 0.99 },
    { label: '25th',   v: 1.35 },
    { label: 'median', v: 1.96 },
    { label: '75th',   v: 3.00 },
    { label: '90th',   v: 4.38 },
    { label: '95th',   v: 5.40 },
    { label: '99th',   v: 9.38 },
  ],
  mostMovement: [
    { date: '2025-04-07', pct: 23.28 },
    { date: '2020-03-18', pct: 20.12 },
    { date: '2020-03-17', pct: 19.80 },
    { date: '2020-03-13', pct: 19.66 },
    { date: '2020-03-12', pct: 19.58 },
  ],
  leastMovement: [
    { date: '2017-11-24', pct: 0.25 },
    { date: '2023-07-03', pct: 0.26 },
    { date: '2016-11-25', pct: 0.32 },
    { date: '2019-12-24', pct: 0.33 },
    { date: '2023-11-24', pct: 0.41 },
  ],
};

function SpyMovementReference() {
  const maxYear = Math.max(...SPY_REF.byYear.map(r => r.p));
  const maxDay  = Math.max(...SPY_REF.byWeekday.map(r => r.p));

  const header    = { fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', color: '#94a3b8', textTransform: 'uppercase', marginBottom: 12 };
  const subHeader = { fontSize: 10, fontWeight: 600, letterSpacing: '0.08em', color: '#64748b', textTransform: 'uppercase', marginBottom: 10 };
  const cardBase  = { background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 10 };
  const mono      = { fontFamily: "'JetBrains Mono', monospace", fontVariantNumeric: 'tabular-nums' };

  const BarRow = ({ label, pct, days, max }) => {
    const width = Math.min(100, (pct / max) * 100);
    return (
      <div style={{ display: 'grid', gridTemplateColumns: '52px 1fr 68px 50px', alignItems: 'center', gap: 10, padding: '6px 0' }}>
        <span style={{ fontSize: 12, color: '#cbd5e1', ...mono, fontWeight: 600 }}>{label}</span>
        <div style={{ height: 6, borderRadius: 3, background: 'rgba(255,255,255,0.04)', overflow: 'hidden' }}>
          <div style={{ height: '100%', width: `${width}%`, background: 'linear-gradient(90deg, rgba(10,132,255,0.75), rgba(10,132,255,0.4))', borderRadius: 3 }} />
        </div>
        <span style={{ fontSize: 12, color: '#e2e8f0', ...mono, fontWeight: 600, textAlign: 'right' }}>{pct.toFixed(2)}%</span>
        <span style={{ fontSize: 10, color: '#64748b', ...mono, textAlign: 'right' }}>{days}d</span>
      </div>
    );
  };

  return (
    <>
      <div style={{ ...header, marginTop: 12 }}>SPY Movement Reference</div>
      <div style={{ fontSize: 11, color: '#64748b', marginBottom: 14, lineHeight: 1.5, ...mono }}>{SPY_REF.source}</div>

      {/* Headline tiles — reuses MetricCard for native look */}
      <div className="admin-metrics-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 14 }}>
        <MetricCard label="AVG PATH / DAY"    value={`${SPY_REF.headline.avgPath.toFixed(2)}%`}    sub="total back-and-forth travel" />
        <MetricCard label="MEDIAN PATH / DAY" value={`${SPY_REF.headline.medianPath.toFixed(2)}%`} sub="typical trading day" />
        <MetricCard label="AVG HIGH-LOW RANGE" value={`${SPY_REF.headline.avgRange.toFixed(2)}%`}  sub="net daily range" />
        <MetricCard label="PATH / RANGE"      value={`${SPY_REF.headline.pathToRange.toFixed(2)}x`} sub="travels ~3× its net range" />
      </div>

      <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 24, lineHeight: 1.6 }}>
        <span style={{ color: '#cbd5e1', fontWeight: 600 }}>Path</span> = sum of absolute 15-min close-to-close moves within the day — total travel, not just high-low range.
      </div>

      {/* By year + by weekday — stack on mobile via .ref-split */}
      <div className="ref-split" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12, marginBottom: 24 }}>
        <div style={{ ...cardBase, padding: 16 }}>
          <div style={subHeader}>By year</div>
          {SPY_REF.byYear.map(r => <BarRow key={r.y} label={r.y} pct={r.p} days={r.d} max={maxYear} />)}
        </div>
        <div style={{ ...cardBase, padding: 16 }}>
          <div style={subHeader}>By weekday</div>
          {SPY_REF.byWeekday.map(r => <BarRow key={r.w} label={r.w} pct={r.p} days={r.d} max={maxDay} />)}
        </div>
      </div>

      {/* Distribution chips */}
      <div style={header}>Distribution (daily path %)</div>
      <div style={{ ...cardBase, padding: '14px 16px', marginBottom: 24, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {SPY_REF.distribution.map(d => {
          const isMedian = d.label === 'median';
          return (
            <div key={d.label} style={{
              display: 'inline-flex', alignItems: 'baseline', gap: 6,
              padding: '6px 12px', borderRadius: 20,
              background: isMedian ? 'rgba(10,132,255,0.14)' : 'rgba(255,255,255,0.03)',
              border: `1px solid ${isMedian ? 'rgba(10,132,255,0.3)' : 'rgba(255,255,255,0.07)'}`,
            }}>
              <span style={{ fontSize: 10, color: isMedian ? '#4da6ff' : '#94a3b8', letterSpacing: '0.04em', textTransform: 'uppercase', fontWeight: 600 }}>{d.label}</span>
              <span style={{ fontSize: 13, fontWeight: 700, color: isMedian ? '#4da6ff' : '#e2e8f0', ...mono }}>{d.v.toFixed(2)}%</span>
            </div>
          );
        })}
      </div>

      {/* Extremes — side-by-side on desktop, stacked on mobile */}
      <div className="ref-split" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
        <div style={{ ...cardBase, padding: 16 }}>
          <div style={subHeader}>Most movement</div>
          {SPY_REF.mostMovement.map((d, i, arr) => (
            <div key={d.date} style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 0', borderBottom: i < arr.length - 1 ? '1px solid rgba(255,255,255,0.04)' : 'none' }}>
              <span style={{ fontSize: 12, color: '#cbd5e1', ...mono }}>{d.date}</span>
              <span style={{ fontSize: 12, fontWeight: 700, color: '#E24B4A', ...mono }}>{d.pct.toFixed(2)}%</span>
            </div>
          ))}
        </div>
        <div style={{ ...cardBase, padding: 16 }}>
          <div style={subHeader}>Least movement</div>
          {SPY_REF.leastMovement.map((d, i, arr) => (
            <div key={d.date} style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 0', borderBottom: i < arr.length - 1 ? '1px solid rgba(255,255,255,0.04)' : 'none' }}>
              <span style={{ fontSize: 12, color: '#cbd5e1', ...mono }}>{d.date}</span>
              <span style={{ fontSize: 12, fontWeight: 700, color: '#639922', ...mono }}>{d.pct.toFixed(2)}%</span>
            </div>
          ))}
        </div>
      </div>

      <div style={{ fontSize: 10, color: '#64748b', lineHeight: 1.6, marginTop: 4 }}>
        Reference only. Not financial advice. Historical movement does not predict future movement. SPY as S&P 500 proxy.
      </div>
    </>
  );
}

function OverviewStatsTab() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    apiFetch(`/api/admin/stats?secret=${SECRET}`)
      .then(d => { setStats(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  if (loading) return <div style={{ color: '#64748b', padding: 40, textAlign: 'center' }}>Loading metrics...</div>;
  if (!stats) return <div style={{ color: '#E24B4A', padding: 40, textAlign: 'center' }}>Failed to load stats</div>;

  const { users, engagement, platform } = stats;

  return (
    <div>
      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', color: '#94a3b8', textTransform: 'uppercase', marginBottom: 12 }}>Key Metrics</div>
      <div className="admin-metrics-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 28 }}>
        <MetricCard label="Total Users" value={users.total} sub={`${users.signups_today} today`} />
        <MetricCard label="DAU" value={engagement.dau_today} sub={`${engagement.dau_mau_ratio}% DAU/MAU`} />
        <MetricCard label="WAU" value={engagement.wau} sub="Last 7 days" />
        <MetricCard label="MAU" value={engagement.mau} sub="Last 30 days" />
      </div>
      <div className="admin-metrics-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 28 }}>
        <MetricCard label="Verified" value={users.verified} sub={`${users.total > 0 ? Math.round(users.verified / users.total * 100) : 0}% of total`} />
        <MetricCard label="7-Day Retention" value={`${engagement.retention_7d_pct}%`} sub="Returned within 7d" />
        <MetricCard label="Watchlist Users" value={users.watchlist_users} sub={`${users.total > 0 ? Math.round(users.watchlist_users / users.total * 100) : 0}% adoption`} />
        <MetricCard label="Tickers" value={platform.tickers_total} sub={`${platform.tickers_morning_fetch} morning fetch`} />
      </div>

      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', color: '#94a3b8', textTransform: 'uppercase', marginBottom: 12 }}>Signup Trend (30 days)</div>
      <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 10, padding: 16, marginBottom: 28 }}>
        {users.signup_trend && users.signup_trend.length > 0 ? (
          <Bar
            data={{
              labels: users.signup_trend.map(d => d.date.slice(5)),
              datasets: [{ label: 'Signups', data: users.signup_trend.map(d => d.count), backgroundColor: 'rgba(48,209,88,0.85)', borderRadius: 3 }],
            }}
            options={{
              responsive: true, plugins: { legend: { display: false } },
              scales: {
                x: { ticks: { color: '#94a3b8', font: { size: 11 } }, grid: { display: false } },
                y: { ticks: { color: '#94a3b8', font: { size: 11 } }, grid: { color: 'rgba(255,255,255,0.06)' }, beginAtZero: true },
              },
            }}
            height={80}
          />
        ) : <div style={{ color: '#64748b', fontSize: 12, textAlign: 'center', padding: 20 }}>No signup data yet</div>}
      </div>

      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', color: '#94a3b8', textTransform: 'uppercase', marginBottom: 12 }}>DAU Trend (14 days)</div>
      <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 10, padding: 16, marginBottom: 28 }}>
        {engagement.dau_trend && engagement.dau_trend.length > 0 ? (
          <Line
            data={{
              labels: engagement.dau_trend.map(d => d.date.slice(5)),
              datasets: [{ label: 'DAU', data: engagement.dau_trend.map(d => d.dau), borderColor: '#3b9eff', backgroundColor: 'rgba(59,158,255,0.15)', fill: true, tension: 0.3, pointRadius: 5, pointBackgroundColor: '#3b9eff' }],
            }}
            options={{
              responsive: true, plugins: { legend: { display: false } },
              scales: {
                x: { ticks: { color: '#94a3b8', font: { size: 11 } }, grid: { display: false } },
                y: { ticks: { color: '#94a3b8', font: { size: 11 } }, grid: { color: 'rgba(255,255,255,0.06)' }, beginAtZero: true },
              },
            }}
            height={80}
          />
        ) : <div style={{ color: '#64748b', fontSize: 12, textAlign: 'center', padding: 20 }}>No DAU data yet</div>}
      </div>

      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', color: '#94a3b8', textTransform: 'uppercase', marginBottom: 12 }}>Top Users by Logins</div>
      <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 10, padding: 16, marginBottom: 28, overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, fontFamily: "'JetBrains Mono', monospace" }}>
          <thead>
            <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
              <th style={{ textAlign: 'left', padding: '8px 12px', color: '#94a3b8', fontWeight: 600 }}>Email</th>
              <th style={{ textAlign: 'right', padding: '8px 12px', color: '#94a3b8', fontWeight: 600 }}>Logins</th>
              <th style={{ textAlign: 'right', padding: '8px 12px', color: '#94a3b8', fontWeight: 600 }}>Last Active</th>
              <th style={{ textAlign: 'right', padding: '8px 12px', color: '#94a3b8', fontWeight: 600 }}>Joined</th>
            </tr>
          </thead>
          <tbody>
            {engagement.top_users.map((u, i) => (
              <tr key={i} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                <td style={{ padding: '8px 12px', color: '#e2e8f0' }}>{u.email}</td>
                <td style={{ textAlign: 'right', padding: '8px 12px', color: '#e2e8f0', fontWeight: 700 }}>{u.logins}</td>
                <td style={{ textAlign: 'right', padding: '8px 12px', color: '#94a3b8' }}>{u.last_login ? new Date(u.last_login).toLocaleDateString() : '—'}</td>
                <td style={{ textAlign: 'right', padding: '8px 12px', color: '#94a3b8' }}>{u.created_at ? new Date(u.created_at).toLocaleDateString() : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', color: '#94a3b8', textTransform: 'uppercase', marginBottom: 12 }}>Platform Summary</div>
      <div style={{ background: 'rgba(48,209,88,0.04)', border: '1px solid rgba(48,209,88,0.12)', borderRadius: 10, padding: 20 }}>
        <div style={{ fontSize: 13, color: '#cbd5e1', lineHeight: 1.8 }}>
          <div>• <span style={{ color: '#e2e8f0', fontWeight: 600 }}>{users.total}</span> organic users with zero marketing spend</div>
          <div>• <span style={{ color: '#e2e8f0', fontWeight: 600 }}>{engagement.dau_mau_ratio}%</span> DAU/MAU ratio (industry benchmark: 20%+ = strong engagement)</div>
          <div>• <span style={{ color: '#e2e8f0', fontWeight: 600 }}>{engagement.retention_7d_pct}%</span> 7-day retention rate</div>
          <div>• <span style={{ color: '#e2e8f0', fontWeight: 600 }}>{platform.tickers_total}</span> tickers with real-time GEX analysis across all major sectors</div>
          <div>• <span style={{ color: '#e2e8f0', fontWeight: 600 }}>98%</span> API cost reduction through infrastructure optimization</div>
          <div>• <span style={{ color: '#e2e8f0', fontWeight: 600 }}>{users.watchlist_users}</span> users actively using watchlist feature ({users.total > 0 ? Math.round(users.watchlist_users / users.total * 100) : 0}% adoption)</div>
        </div>
      </div>

      {/* Divider + static reference panel */}
      <div style={{ height: 1, background: 'rgba(255,255,255,0.06)', margin: '32px 0 28px' }} />
      <SpyMovementReference />
    </div>
  );
}

// ─── Operational Tab ──────────────────────────────────────────────────────────

function HolidaysSection() {
  const [holidays, setHolidays] = useState([]);
  const [form, setForm] = useState({ date: '', name: '', type: 'full_close', early_close_time: '13:00' });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const load = () => {
    apiFetch(`/api/admin/holidays?secret=${SECRET}`)
      .then(d => setHolidays(d?.holidays || []))
      .catch(() => setHolidays([]));
  };
  useEffect(load, []);

  const submit = async () => {
    setErr('');
    if (!form.date || !form.name) { setErr('Date and name required'); return; }
    setBusy(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/holidays?secret=${SECRET}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Failed');
      setForm({ date: '', name: '', type: 'full_close', early_close_time: '13:00' });
      load();
    } catch (e) {
      setErr(e.message);
    } finally { setBusy(false); }
  };

  const remove = async (date) => {
    if (!window.confirm(`Delete holiday on ${date}?`)) return;
    await fetch(`${API_BASE}/api/admin/holidays/${date}?secret=${SECRET}`, { method: 'DELETE' });
    load();
  };

  return (
    <>
      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', color: '#94a3b8', textTransform: 'uppercase', marginBottom: 12 }}>Market Holidays</div>
      <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 10, padding: 16, marginBottom: 28 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr 140px 110px auto', gap: 8, marginBottom: 12 }}>
          <input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })}
            style={{ padding: '6px 10px', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 6, color: '#e2e8f0', fontSize: 12, fontFamily: "'JetBrains Mono', monospace" }} />
          <input type="text" placeholder="Holiday name (e.g. Thanksgiving)" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
            style={{ padding: '6px 10px', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 6, color: '#e2e8f0', fontSize: 12 }} />
          <select value={form.type} onChange={e => setForm({ ...form, type: e.target.value })}
            style={{ padding: '6px 10px', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 6, color: '#e2e8f0', fontSize: 12 }}>
            <option value="full_close">Full close</option>
            <option value="early_close">Early close</option>
          </select>
          <input type="time" disabled={form.type !== 'early_close'} value={form.early_close_time} onChange={e => setForm({ ...form, early_close_time: e.target.value })}
            style={{ padding: '6px 10px', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 6, color: '#e2e8f0', fontSize: 12, fontFamily: "'JetBrains Mono', monospace", opacity: form.type === 'early_close' ? 1 : 0.4 }} />
          <button onClick={submit} disabled={busy} style={{ padding: '6px 14px', background: 'rgba(48,209,88,0.15)', border: '1px solid rgba(48,209,88,0.3)', borderRadius: 6, color: '#30d158', fontSize: 11, fontWeight: 600, cursor: busy ? 'wait' : 'pointer' }}>
            {busy ? '...' : 'Add'}
          </button>
        </div>
        {err && <div style={{ color: '#ff453a', fontSize: 11, marginBottom: 10 }}>{err}</div>}
        {holidays.length === 0 ? (
          <div style={{ color: '#64748b', fontSize: 12, textAlign: 'center', padding: 14 }}>No upcoming holidays.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {holidays.map(h => (
              <div key={h.date} style={{ display: 'grid', gridTemplateColumns: '120px 1fr 110px 110px auto', gap: 8, alignItems: 'center', padding: '8px 10px', background: 'rgba(255,255,255,0.02)', borderRadius: 6 }}>
                <span style={{ fontSize: 12, color: '#e2e8f0', fontFamily: "'JetBrains Mono', monospace", fontWeight: 600 }}>{h.date}</span>
                <span style={{ fontSize: 12, color: '#cbd5e1' }}>{h.name}</span>
                <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 4, letterSpacing: '0.04em', background: h.type === 'full_close' ? 'rgba(255,69,58,0.12)' : 'rgba(245,158,11,0.12)', color: h.type === 'full_close' ? '#ff453a' : '#f59e0b', border: `1px solid ${h.type === 'full_close' ? 'rgba(255,69,58,0.25)' : 'rgba(245,158,11,0.25)'}`, textAlign: 'center' }}>
                  {h.type === 'full_close' ? 'FULL CLOSE' : 'EARLY CLOSE'}
                </span>
                <span style={{ fontSize: 11, color: '#94a3b8', fontFamily: "'JetBrains Mono', monospace" }}>{h.early_close_time || '—'}</span>
                <button onClick={() => remove(h.date)} style={{ padding: '4px 10px', background: 'transparent', border: '1px solid rgba(255,69,58,0.25)', borderRadius: 5, color: '#ff453a', fontSize: 10, fontWeight: 600, cursor: 'pointer' }}>Delete</button>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}


function OperationalTab() {
  const [credits, setCredits] = useState(null);
  const [cacheStatus, setCacheStatus] = useState(null);
  const [trapStatus, setTrapStatus] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      apiFetch(`/api/admin/credits?secret=${SECRET}`).catch(() => null),
      apiFetch(`/api/admin/cache/status?secret=${SECRET}`),
      apiFetch(`/api/admin/trap-status?secret=${SECRET}`),
      apiFetch(`/api/admin/stats?secret=${SECRET}`),
    ]).then(([cred, cache, trap, st]) => {
      setCredits(cred);
      setCacheStatus(cache);
      setTrapStatus(trap);
      setStats(st);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  if (loading) return <div style={{ color: '#94a3b8', padding: 40, textAlign: 'center' }}>Loading operational data...</div>;

  const tickers = cacheStatus?.tickers || {};
  const tickerList = Object.entries(tickers);
  const totalCached = cacheStatus?.cached_tickers || 0;
  const premiumCount = tickerList.filter(([, v]) => v.data_quality === 'premium').length;
  const degradedCount = tickerList.filter(([, v]) => v.data_quality === 'degraded').length;
  const staleCount = tickerList.filter(([, v]) => v.stale).length;
  const freshCount = tickerList.filter(([, v]) => !v.stale && v.expires_in_seconds > 0).length;
  const expiredCount = tickerList.filter(([, v]) => v.expires_in_seconds <= 0).length;

  const credColor = credits?.status === 'healthy' ? '#30d158' : credits?.status === 'warning' ? '#ff9f0a' : '#ff453a';
  const credBg = credits?.status === 'healthy' ? 'rgba(48,209,88,0.12)' : credits?.status === 'warning' ? 'rgba(255,159,10,0.12)' : 'rgba(255,69,58,0.12)';
  const credBorder = credits?.status === 'healthy' ? 'rgba(48,209,88,0.25)' : credits?.status === 'warning' ? 'rgba(255,159,10,0.25)' : 'rgba(255,69,58,0.25)';

  return (
    <div>
      {/* API Credits */}
      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', color: '#94a3b8', textTransform: 'uppercase', marginBottom: 12 }}>API Credits (MarketData)</div>
      <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 10, padding: 20, marginBottom: 28 }}>
        {credits ? (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <div>
                <span style={{ fontSize: 28, fontWeight: 700, color: credColor, fontFamily: "'JetBrains Mono', monospace" }}>{credits.used.toLocaleString()}</span>
                <span style={{ fontSize: 14, color: '#64748b', fontFamily: "'JetBrains Mono', monospace" }}> / {credits.limit.toLocaleString()}</span>
              </div>
              <span style={{ fontSize: 10, fontWeight: 700, padding: '4px 10px', borderRadius: 6, letterSpacing: '0.06em', background: credBg, color: credColor, border: `1px solid ${credBorder}` }}>{credits.status.toUpperCase()}</span>
            </div>
            <div style={{ height: 8, background: 'rgba(255,255,255,0.04)', borderRadius: 4, overflow: 'hidden', marginBottom: 12 }}>
              <div style={{ height: '100%', width: `${Math.min(credits.usage_pct, 100)}%`, borderRadius: 4, background: credColor, transition: 'width 1s ease' }} />
            </div>
            <div style={{ display: 'flex', gap: 24, fontSize: 12, fontFamily: "'JetBrains Mono', monospace" }}>
              <div>
                <div style={{ color: '#64748b', fontSize: 10, marginBottom: 2 }}>Used</div>
                <div style={{ color: '#e2e8f0', fontWeight: 600 }}>{credits.used.toLocaleString()}</div>
              </div>
              <div>
                <div style={{ color: '#64748b', fontSize: 10, marginBottom: 2 }}>Remaining</div>
                <div style={{ color: '#e2e8f0', fontWeight: 600 }}>{credits.remaining.toLocaleString()}</div>
              </div>
              <div>
                <div style={{ color: '#64748b', fontSize: 10, marginBottom: 2 }}>Usage</div>
                <div style={{ color: '#e2e8f0', fontWeight: 600 }}>{credits.usage_pct}%</div>
              </div>
              <div>
                <div style={{ color: '#64748b', fontSize: 10, marginBottom: 2 }}>Resets</div>
                <div style={{ color: '#e2e8f0', fontWeight: 600 }}>{credits.reset_time}</div>
              </div>
            </div>
          </div>
        ) : (
          <div style={{ color: '#64748b', fontSize: 12, textAlign: 'center', padding: 10 }}>Failed to load credit data</div>
        )}
      </div>

      {/* Holidays */}
      <HolidaysSection />

      {/* Cache Health Summary */}
      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', color: '#94a3b8', textTransform: 'uppercase', marginBottom: 12 }}>Cache Health</div>
      <div className="admin-metrics-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 28 }}>
        <MetricCard label="Cached Tickers" value={totalCached} sub={`of ${stats?.platform?.tickers_total || 100}`} />
        <MetricCard label="Premium" value={premiumCount} sub={`${degradedCount} degraded`} />
        <MetricCard label="Fresh" value={freshCount} sub={`${expiredCount} expired`} />
        <MetricCard label="Cache TTL" value={`${(cacheStatus?.cache_ttl_seconds || 1800) / 60}m`} sub={`${staleCount} stale`} />
      </div>

      {/* Trap Zone Status */}
      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', color: '#94a3b8', textTransform: 'uppercase', marginBottom: 12 }}>Trap Zone (SPY / QQQ)</div>
      <div className="admin-metrics-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10, marginBottom: 28 }}>
        {trapStatus?.today && Object.entries(trapStatus.today).map(([ticker, trap]) => (
          <div key={ticker} style={{
            background: 'rgba(255,255,255,0.02)',
            border: `1px solid ${trap.active ? 'rgba(255,159,10,0.3)' : 'rgba(255,255,255,0.06)'}`,
            borderRadius: 10, padding: 16,
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <span style={{ fontSize: 14, fontWeight: 800, color: '#e2e8f0' }}>{ticker}</span>
              <span style={{
                fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', padding: '3px 8px', borderRadius: 4,
                background: trap.active ? 'rgba(255,159,10,0.15)' : 'rgba(255,255,255,0.04)',
                color: trap.active ? '#ff9f0a' : '#64748b',
                border: `1px solid ${trap.active ? 'rgba(255,159,10,0.3)' : 'rgba(255,255,255,0.08)'}`,
              }}>{trap.active ? 'ACTIVE' : 'INACTIVE'}</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, fontSize: 11 }}>
              <div>
                <div style={{ color: '#64748b', marginBottom: 2 }}>Range</div>
                <div style={{ color: '#e2e8f0', fontWeight: 700 }}>${trap.low}–${trap.high}</div>
              </div>
              <div>
                <div style={{ color: '#64748b', marginBottom: 2 }}>Compression</div>
                <div style={{ color: trap.compression_pct > 50 ? '#ff9f0a' : '#94a3b8', fontWeight: 700 }}>{trap.compression_pct}%</div>
              </div>
              <div>
                <div style={{ color: '#64748b', marginBottom: 2 }}>Bias</div>
                <div style={{ color: trap.bias === 'bearish' ? '#ff453a' : trap.bias === 'bullish' ? '#30d158' : '#94a3b8', fontWeight: 700 }}>{trap.bias}</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Full Cache Table */}
      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', color: '#94a3b8', textTransform: 'uppercase', marginBottom: 12 }}>Cache Detail ({tickerList.length} tickers)</div>
      <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 10, padding: 16, marginBottom: 28, overflowX: 'auto', maxHeight: 400, overflowY: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11, fontFamily: "'JetBrains Mono', monospace" }}>
          <thead style={{ position: 'sticky', top: 0, background: '#0a0f1e' }}>
            <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
              <th style={{ textAlign: 'left', padding: '6px 10px', color: '#94a3b8', fontWeight: 600 }}>Ticker</th>
              <th style={{ textAlign: 'right', padding: '6px 10px', color: '#94a3b8', fontWeight: 600 }}>Spot</th>
              <th style={{ textAlign: 'right', padding: '6px 10px', color: '#94a3b8', fontWeight: 600 }}>Age</th>
              <th style={{ textAlign: 'right', padding: '6px 10px', color: '#94a3b8', fontWeight: 600 }}>TTL</th>
              <th style={{ textAlign: 'center', padding: '6px 10px', color: '#94a3b8', fontWeight: 600 }}>Quality</th>
              <th style={{ textAlign: 'center', padding: '6px 10px', color: '#94a3b8', fontWeight: 600 }}>Status</th>
            </tr>
          </thead>
          <tbody>
            {tickerList
              .sort(([,a], [,b]) => (a.age_seconds || 0) - (b.age_seconds || 0))
              .map(([ticker, info]) => (
              <tr key={ticker} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                <td style={{ padding: '6px 10px', color: '#e2e8f0', fontWeight: 600 }}>{ticker}</td>
                <td style={{ textAlign: 'right', padding: '6px 10px', color: '#cbd5e1' }}>
                  {info.spot_price ? `$${info.spot_price.toLocaleString(undefined, {maximumFractionDigits: 2})}` : '—'}
                </td>
                <td style={{ textAlign: 'right', padding: '6px 10px', color: info.age_seconds > 1800 ? '#ff453a' : '#94a3b8' }}>
                  {info.age_seconds ? `${Math.round(info.age_seconds / 60)}m` : '—'}
                </td>
                <td style={{ textAlign: 'right', padding: '6px 10px', color: info.expires_in_seconds > 0 ? '#30d158' : '#ff453a' }}>
                  {info.expires_in_seconds > 0 ? `${Math.round(info.expires_in_seconds / 60)}m` : 'expired'}
                </td>
                <td style={{ textAlign: 'center', padding: '6px 10px' }}>
                  <span style={{
                    fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 3,
                    background: info.data_quality === 'premium' ? 'rgba(48,209,88,0.12)' : 'rgba(255,159,10,0.12)',
                    color: info.data_quality === 'premium' ? '#30d158' : '#ff9f0a',
                    border: `1px solid ${info.data_quality === 'premium' ? 'rgba(48,209,88,0.25)' : 'rgba(255,159,10,0.25)'}`,
                  }}>{info.data_quality || '—'}</span>
                </td>
                <td style={{ textAlign: 'center', padding: '6px 10px' }}>
                  <span style={{
                    fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 3,
                    background: info.stale ? 'rgba(255,69,58,0.12)' : 'rgba(48,209,88,0.12)',
                    color: info.stale ? '#ff453a' : '#30d158',
                  }}>{info.stale ? 'STALE' : 'FRESH'}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Admin Actions */}
      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', color: '#94a3b8', textTransform: 'uppercase', marginBottom: 12 }}>Admin Actions</div>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 28 }}>
        <button onClick={() => {
          fetch(`${API_BASE}/api/admin/cache/clear?secret=${SECRET}`, { method: 'POST' })
            .then(() => { alert('Cache cleared'); window.location.reload(); }).catch(e => alert('Failed: ' + e));
        }} style={{ padding: '10px 18px', borderRadius: 8, background: 'rgba(226,75,74,0.1)', border: '1px solid rgba(226,75,74,0.25)', color: '#E24B4A', fontSize: 12, fontWeight: 600, fontFamily: "'JetBrains Mono', monospace", cursor: 'pointer' }}>
          Clear Cache
        </button>
        <button onClick={() => {
          fetch(`${API_BASE}/api/admin/save-levels?secret=${SECRET}`, { method: 'POST' })
            .then(r => r.json()).then(d => alert(JSON.stringify(d, null, 2))).catch(e => alert('Failed: ' + e));
        }} style={{ padding: '10px 18px', borderRadius: 8, background: 'rgba(48,209,88,0.1)', border: '1px solid rgba(48,209,88,0.25)', color: '#30d158', fontSize: 12, fontWeight: 600, fontFamily: "'JetBrains Mono', monospace", cursor: 'pointer' }}>
          Save Levels to DB
        </button>
        <button onClick={() => { window.location.reload(); }} style={{ padding: '10px 18px', borderRadius: 8, background: 'rgba(10,132,255,0.1)', border: '1px solid rgba(10,132,255,0.25)', color: '#0a84ff', fontSize: 12, fontWeight: 600, fontFamily: "'JetBrains Mono', monospace", cursor: 'pointer' }}>
          Refresh Data
        </button>
      </div>
    </div>
  );
}

// ─── Historical Analysis Tab (wraps existing content) ─────────────────────────

function HistoricalTab() {
  const [ticker, setTicker] = useState('SPY');
  const [tab, setTab] = useState('verdict');
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [summaryError, setSummaryError] = useState(null);

  useEffect(() => {
    apiFetch(`/api/admin/historical/summary?secret=${SECRET}`)
      .then(d => { setSummary(d); setSummaryLoading(false); })
      .catch(e => { setSummaryError(e.message); setSummaryLoading(false); });
  }, []);

  const tabs = ['verdict', 'overview', 'patterns', 'ranges', 'seasonality', 'interpret'];
  const tabLabels = { verdict: 'Today', overview: 'Overview', patterns: 'Patterns', ranges: 'Range sizing', seasonality: 'Seasonality', interpret: 'AI Interpret' };
  const tickers = ['SPY', 'QQQ', 'IWM'];

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        {tickers.map(t => (
          <button key={t} className={`ticker-btn${ticker === t ? ' active' : ''}`} onClick={() => setTicker(t)}>{t}</button>
        ))}
      </div>
      <div style={{ borderBottom: '1px solid rgba(255,255,255,0.06)', marginBottom: 24, display: 'flex' }}>
        {tabs.map(t => (
          <button key={t}
            className={`admin-tab${t === 'interpret' ? ' ai-tab' : t === 'verdict' ? ' today-tab' : ''}${tab === t ? ' active' : ''}`}
            onClick={() => setTab(t)}
          >
            {t === 'interpret' ? '✦ AI INTERPRET' : t === 'verdict' ? '● TODAY' : tabLabels[t].toUpperCase()}
          </button>
        ))}
      </div>
      {summaryLoading && tab === 'overview' ? <Loading /> :
       summaryError && tab === 'overview' ? <ErrorMsg msg={summaryError} /> :
       tab === 'verdict' ? <VerdictTab /> :
       tab === 'overview' ? <HistoricalOverviewTab summary={summary} ticker={ticker} /> :
       tab === 'patterns' ? <PatternsTab ticker={ticker} /> :
       tab === 'ranges' ? <RangesTab ticker={ticker} /> :
       tab === 'seasonality' ? <SeasonalityTab ticker={ticker} /> :
       tab === 'interpret' ? <InterpretTab ticker={ticker} /> : null}
    </div>
  );
}

// ─── Main Dashboard ──────────────────────────────────────────────────────────

export default function AdminDashboard({ onBack }) {
  const [unlocked, setUnlocked] = useState(() => sessionStorage.getItem('gl_admin_unlocked') === '1');
  const [activeTab, setActiveTab] = useState('niw');

  const handleUnlock = () => {
    sessionStorage.setItem('gl_admin_unlocked', '1');
    setUnlocked(true);
  };

  if (!unlocked) return <SecretGate onUnlock={handleUnlock} />;

  const topTabs = [
    { id: 'niw', label: 'Overview' },
    { id: 'ops', label: 'Operational' },
    { id: 'historical', label: 'Historical Analysis' },
  ];

  return (
    <div style={{
      minHeight: '100vh', background: '#0a0f1e',
      color: '#e2e8f0', fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;500;600;700&display=swap');
        .admin-tab { padding: 10px 18px; font-size: 12px; font-weight: 500; letter-spacing: 0.06em; cursor: pointer; border: none; background: none; color: #475569; border-bottom: 2px solid transparent; transition: all 0.15s; font-family: inherit; }
        .admin-tab:hover { color: #94a3b8; }
        .admin-tab.active { color: #e2e8f0; border-bottom-color: #0a84ff; }
        .admin-tab.today-tab.active { border-bottom-color: #639922; color: #30d158; }
        .admin-tab.ai-tab.active { border-bottom-color: #bf5af2; color: #bf5af2; }
        .ticker-btn { padding: 6px 14px; font-size: 12px; font-weight: 600; letter-spacing: 0.08em; cursor: pointer; border: 1px solid rgba(255,255,255,0.08); border-radius: 6px; background: none; color: #64748b; transition: all 0.15s; font-family: inherit; }
        .ticker-btn:hover { border-color: rgba(255,255,255,0.16); color: #94a3b8; }
        .ticker-btn.active { background: rgba(55,138,221,0.12); border-color: rgba(55,138,221,0.3); color: #4da6ff; }
        .back-btn { background: none; border: none; color: #475569; font-size: 12px; cursor: pointer; font-family: inherit; letter-spacing: 0.05em; padding: 0; }
        .back-btn:hover { color: #94a3b8; }
        @media (max-width: 768px) { .admin-metrics-grid { grid-template-columns: repeat(2, 1fr) !important; } }
        @media (max-width: 768px) { .ref-split { grid-template-columns: 1fr !important; } }
      `}</style>

      {/* Header */}
      <div style={{ borderBottom: '1px solid rgba(255,255,255,0.06)', padding: '14px 28px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <button className="back-btn" onClick={onBack}>←</button>
          <GammaLensLogo size={24} />
          <span style={{ fontSize: 15, fontWeight: 700, letterSpacing: '-0.3px', color: '#f2f2f7' }}>GammaLens</span>
          <span style={{ fontSize: 10, fontWeight: 700, color: '#0a84ff', letterSpacing: '0.1em', background: 'rgba(10,132,255,0.12)', border: '1px solid rgba(10,132,255,0.25)', borderRadius: '4px', padding: '2px 8px' }}>ADMIN</span>
        </div>
      </div>

      {/* Top-level tabs */}
      <div style={{ borderBottom: '1px solid rgba(255,255,255,0.08)', padding: '0 28px', display: 'flex', gap: 0 }}>
        {topTabs.map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)} style={{
            padding: '12px 20px', fontSize: 12, fontWeight: 600,
            fontFamily: "'JetBrains Mono', monospace", letterSpacing: '0.04em',
            color: activeTab === t.id ? '#0a84ff' : '#64748b',
            background: 'none', border: 'none',
            borderBottom: activeTab === t.id ? '2px solid #0a84ff' : '2px solid transparent',
            cursor: 'pointer', transition: 'all 0.15s',
          }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div style={{ padding: '24px 28px', maxWidth: 1100, margin: '0 auto' }}>
        {activeTab === 'niw' && <OverviewStatsTab />}
        {activeTab === 'ops' && <OperationalTab />}
        {activeTab === 'historical' && <HistoricalTab />}
      </div>
    </div>
  );
}
